/**
 * frontend/dashboards/PlayerDashboard.jsx
 * SoundDAO — Player (Fan) dashboard — Sprint 5C Part 2 redesign
 *
 * Live data from Supabase via backend API + real-time subscription on balances table.
 *
 * Layout (Sprint 6A polish, Option A): one 560px column (--player-col) shared by every block.
 *
 * Sections:
 *   1. Player card (.pc-card) — avatar/initials, display name, rank chip ("Title · Lv N"),
 *      next rank, global rank; stat row: SOUND (+today) · Creators (scout badges) ·
 *      Streak (or Hot Streak with timer) · Vault (30-day bar). Streak and rank appear once.
 *   2. Your next move banner
 *   3. Missions (MissionCard in .pc-missions)
 *   4. Hot artists — top 3 creators by heat_score not yet recruited
 *   5. Creator Roster — reskinned with stage, heat tier, activity status
 *   6. Playlists link + Dig the Archive (the linked-playlist form moved to /playlists, Sprint 6A)
 *   7. Recruit & Refer
 *   8. Edit Profile (mobile)
 *   9. Chatbot (Rex)
 *  10. Rank-up modal
 *
 * Props:
 *   user       — users row ({ id, persona, rank_number, rank_title, display_name, ... })
 *   balance    — initial SOUND balance (from DashboardWrapper)
 *   session    — Supabase auth session
 *   onSettings — navigate to /settings
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { Link, useNavigate } from 'react-router-dom';
import Chatbot from '../Chatbot';
import MissionCard from '../components/MissionCard';
import { isHeatPublic, publicStageHeatLabel } from '../utils/heatDisplay';
import { getDisplayName } from '../utils/displayName';
import { useRates, sound, plus } from '../lib/rates';
import './Dashboard.css';
import '../styles/archive.css';
import { authFetch } from '../lib/authFetch';
import BrandMark from '../components/BrandMark';
import { inviteUrl } from '../lib/constants';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Rank is SERVED BY THE BACKEND (Sprint 6-Zero Milestone 2).
// The client-side RANKS table and getCurrentRank(balance) that used to live here
// were one of four divergent ladders, and computing from `balance` (spendable)
// rather than total_earned meant locking SOUND in the Vault could demote the
// dashboard while the leaderboard held. lib/rankLadder.js is the only ladder now;
// GET /api/leaderboard/players/me returns { big_creative_rank }.


// ── Heat tier pill colors ───────────────────────────────────────────────────────
const HEAT_COLORS = {
  BREAKING: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444' },
  Hot:      { bg: 'rgba(249,115,22,0.15)', color: '#f97316' },
  Warm:     { bg: 'rgba(234,179,8,0.15)',  color: '#eab308' },
  Cool:     { bg: 'rgba(107,114,128,0.15)', color: '#6b7280' },
};


/** Avatar initials: first letter of the first two words, or the first two letters of one word. */
function initialsFor(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  const raw = words.length > 1 ? words[0][0] + words[1][0] : words[0].slice(0, 2);
  return raw.toUpperCase();
}

// ── PlayerDashboard ────────────────────────────────────────────────────────────
export default function PlayerDashboard({ user, balance: initialBalance, session, onSettings }) {
  const user_id  = session?.user?.id || user?.id;
  const navigate = useNavigate();
  const rates    = useRates();

  const [balance,     setBalance]     = useState(initialBalance || 0);
  const [rankUpModal, setRankUpModal] = useState(null); // { title }
  const [loadingData, setLoadingData] = useState(true);
  const [copyDone,    setCopyDone]    = useState(false);

  // Global rank (Task 3.6)
  const [globalRank, setGlobalRank] = useState(null);
  // Big Creative rank, computed server-side by lib/rankLadder.js
  const [bcRank, setBcRank] = useState(null); // { number, title, next_requirement, ... }
  // Mirrors bcRank.number for the realtime listener, whose closure would
  // otherwise capture a stale value (its effect only depends on user_id).
  const bcRankNumberRef = useRef(null);

  // Sprint 5A — Streak + Vault state
  const [streakCount,       setStreakCount]       = useState(0);
  const [hotStreakExpiresAt, setHotStreakExpiresAt] = useState(null);
  const [vaultBalance,      setVaultBalance]      = useState(0);
  const [vaultLockedUntil,  setVaultLockedUntil]  = useState(null);
  const [vaultLoginCount,   setVaultLoginCount]   = useState(0);

  // Sprint 5C Part 2 — new state
  const [todayEarnings,    setTodayEarnings]    = useState(0);
  const [scoutBadgeCount,  setScoutBadgeCount]  = useState(0);
  const [hotArtists,       setHotArtists]       = useState([]);
  const [rosterScoutBadges, setRosterScoutBadges] = useState({}); // { creator_id: true }
  const [recruitCount,     setRecruitCount]     = useState(0);    // from rewards table
  const [rosterEntries,    setRosterEntries]    = useState([]);   // merged recruit + profile data

  const chatRef            = useRef(null);
  const referralSectionRef = useRef(null);

  // ── Load initial data ─────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user_id) return;
    setLoadingData(true);
    try {
      // Balance
      const balRes = await authFetch(`${API_BASE}/api/rewards/balance/${user_id}`);
      if (balRes.ok) {
        const d = await balRes.json();
        setBalance(d.sound_balance || 0);
      }

      // Global rank (Task 3.6)
      const rankRes = await authFetch(`${API_BASE}/api/leaderboard/players/me?user_id=${user_id}`);
      if (rankRes.ok) {
        const rd = await rankRes.json();
        setGlobalRank(rd.global_rank || null);
        setBcRank(rd.big_creative_rank || null);
        if (rd.big_creative_rank) bcRankNumberRef.current = rd.big_creative_rank.number;
      }

      // Streak data from users table
      try {
        const { data: streakRow } = await supabase
          .from('users')
          .select('streak_count, hot_streak_expires_at')
          .eq('id', user_id)
          .maybeSingle();
        if (streakRow) {
          setStreakCount(streakRow.streak_count || 0);
          const exp = streakRow.hot_streak_expires_at;
          setHotStreakExpiresAt(exp && new Date(exp) > new Date() ? exp : null);
        }
      } catch { /* non-fatal */ }

      // Vault data
      try {
        const { data: vaultRow } = await supabase
          .from('vault')
          .select('balance, locked_until, cycle_login_count')
          .eq('user_id', user_id)
          .maybeSingle();
        if (vaultRow) {
          setVaultBalance(vaultRow.balance || 0);
          setVaultLockedUntil(vaultRow.locked_until || null);
          setVaultLoginCount(vaultRow.cycle_login_count || 0);
        }
      } catch { /* non-fatal */ }

      // Today's earnings
      try {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const { data: todayRewards } = await supabase
          .from('rewards')
          .select('amount')
          .eq('user_id', user_id)
          .gte('created_at', todayStart.toISOString());
        const today = (todayRewards || []).reduce((s, r) => s + Number(r.amount), 0);
        setTodayEarnings(today);
      } catch { /* non-fatal */ }

      // Scout badge count
      try {
        const { count } = await supabase
          .from('scout_badges')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user_id);
        setScoutBadgeCount(count || 0);
      } catch { /* non-fatal */ }

      // Scout badges map — for roster icons
      try {
        const { data: badgeRows } = await supabase
          .from('scout_badges')
          .select('creator_id')
          .eq('user_id', user_id);
        const map = {};
        (badgeRows || []).forEach(b => { map[b.creator_id] = true; });
        setRosterScoutBadges(map);
      } catch { /* non-fatal */ }

      // Recruit count + roster entries — from rewards table (always accurate)
      let recruitTargetIds = [];
      try {
        const { data: recruitRewards, count: rCount } = await supabase
          .from('rewards')
          .select('metadata', { count: 'exact' })
          .eq('user_id', user_id)
          .eq('action_type', 'recruit_creator');
        setRecruitCount(rCount || 0);
        recruitTargetIds = (recruitRewards || [])
          .map(r => r.metadata?.target_id)
          .filter(Boolean);
      } catch { /* non-fatal */ }

      // Fetch creator profiles for recruited target_ids
      if (recruitTargetIds.length > 0) {
        try {
          const { data: profiles } = await supabase
            .from('creators')
            .select('user_id, artist_name, genre, heat_tier, heat_score, stage, last_post_date, total_fans')
            .in('user_id', recruitTargetIds);
          const profileMap = {};
          (profiles || []).forEach(p => { profileMap[p.user_id] = p; });
          // Build merged roster: one entry per target_id
          const merged = recruitTargetIds.map(tid => {
            const profile = profileMap[tid];
            if (profile) return { ...profile, profileComplete: true };
            return { user_id: tid, artist_name: null, profileComplete: false };
          });
          setRosterEntries(merged);
        } catch { /* non-fatal */ }
      } else {
        setRosterEntries([]);
      }

      // Hot artists — top 3 by heat_score not yet recruited
      try {
        const { data: hotRows } = await supabase
          .from('creators')
          .select('id, user_id, artist_name, genre, heat_score, heat_tier, total_fans, stage')
          .in('heat_tier', ['Hot', 'BREAKING', 'Warm'])
          .order('heat_score', { ascending: false })
          .limit(10);
        // Filter out recruited creators client-side using rewards-based IDs
        const filtered = (hotRows || []).filter(
          c => !recruitTargetIds.includes(c.user_id)
        ).slice(0, 3);
        setHotArtists(filtered);
      } catch { /* non-fatal */ }

    } catch (err) {
      console.error('[PlayerDashboard] loadData error:', err);
    } finally {
      setLoadingData(false);
    }
  }, [user_id]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Pick up pending chat message or recruited artist from onboarding ────────
  useEffect(() => {
    // Small delay so chatRef is mounted
    const timer = setTimeout(() => {
      const pendingMsg = localStorage.getItem('sounddao_pending_chat_message');
      if (pendingMsg && chatRef.current) {
        localStorage.removeItem('sounddao_pending_chat_message');
        chatRef.current.openWithMessage(pendingMsg);
      }
      // If player just recruited during artist pick, clear the marker
      // (Rex context handles the awareness server-side)
      localStorage.removeItem('sounddao_recruited_artist');
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // ── Real-time balance subscription ───────────────────────────────────────────
  useEffect(() => {
    if (!user_id) return;
    const channel = supabase
      .channel(`player-balances-${user_id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'balances', filter: `user_id=eq.${user_id}` },
        (payload) => {
          const newBalance = Number(payload.new?.sound_balance || 0);
          setBalance(newBalance);

          // Rank-up detection is server-side now. A balance change can no longer
          // imply a rank change (ranks track total_earned and can involve
          // recruits, referrals and creator stages), so re-ask the backend and
          // fire the modal only if the canonical rank number actually rose.
          (async () => {
            try {
              const r = await authFetch(`${API_BASE}/api/leaderboard/players/me?user_id=${user_id}`);
              if (!r.ok) return;
              const rd = await r.json();
              const fresh = rd.big_creative_rank;
              if (!fresh) return;
              const prevNumber = bcRankNumberRef.current;
              if (prevNumber !== null && fresh.number > prevNumber) {
                setRankUpModal({ title: fresh.title, requirement: fresh.next_requirement });
              }
              bcRankNumberRef.current = fresh.number;
              setBcRank(fresh);
            } catch (_) { /* non-fatal — the next load refreshes rank */ }
          })();
        }
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user_id]);

  // ── Derived rank data ─────────────────────────────────────────────────────────
  // Server-computed. Falls back to the persisted users.rank_title while loading,
  // and never invents a rank if the backend could not compute one.
  const currentRank = bcRank || {
    number: user?.rank_number || null,
    title:  user?.rank_title  || null,
  };

  // ── Hot streak countdown ──────────────────────────────────────────────────────
  const [hotStreakLabel, setHotStreakLabel] = useState('');
  useEffect(() => {
    if (!hotStreakExpiresAt) { setHotStreakLabel(''); return; }
    const tick = () => {
      const diff = Math.max(0, new Date(hotStreakExpiresAt) - Date.now());
      if (diff === 0) { setHotStreakLabel(''); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setHotStreakLabel(h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [hotStreakExpiresAt]);

  // ── Mission action handler (routes action buttons to correct destination) ─────
  const handleMissionAction = useCallback((action) => {
    switch (action) {
      case 'recruit_creator':
        navigate('/creators');
        break;
      case 'playlist_share':
      case 'playlist_build':
        // Sprint 6A: playlists moved to /playlists (Built here + Linked).
        navigate('/playlists');
        break;
      case 'refer_player':
        referralSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;
      default:
        break;
    }
  }, [navigate]);

  // ── Referral link helpers ─────────────────────────────────────────────────────
  const referralCode = user?.referral_code;
  const referralLink = referralCode ? inviteUrl(referralCode) : null;

  const handleCopyLink = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 2000);
    }).catch(() => {
      window.prompt('Copy your referral link:', referralLink);
    });
  };

  const tweetText = referralCode
    ? encodeURIComponent(
        `I'm building my roster on @SoundDAO — join Big Creative and let's earn together: ${inviteUrl(referralCode)}`
      )
    : '';

  const handleShareTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${tweetText}`, '_blank', 'noopener');
  };

  const handleShareWhatsApp = () => {
    if (!referralLink) return;
    const msg = encodeURIComponent(
      `Join me on SoundDAO — earn SOUND tokens as a music fan or creator: ${referralLink}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener');
  };

  const hotStreakActive = hotStreakExpiresAt && new Date(hotStreakExpiresAt) > new Date();

  // ── "Your next move" logic (Sprint 6A) ─────────────────────────────────────
  const rosterSectionRef = useRef(null);
  const nextMove = React.useMemo(() => {
    if (recruitCount === 0) {
      return { icon: '🎤', title: `Recruit your first creator — earn ${sound(rates, 'recruit_creator')}`, sub: 'Head to Discover and pick an artist. Paid once they confirm their email and make a first post.', action: 'recruit' };
    }
    // The banner shows exactly what lib/rankLadder.js says is missing — no more
    // mixing a recruit action with an unrelated SOUND threshold.
    if (bcRank?.next_requirement && bcRank.next_reachable) {
      // The backend phrases it "A&R Rep: Recruit 1 more creator…"; the title already names the
      // rank, so drop the leading "{next_title}:" from the sub.
      const title = String(bcRank.next_title || '');
      const req = String(bcRank.next_requirement);
      const sub = title && req.startsWith(`${title}:`) ? req.slice(title.length + 1).trimStart() : req;
      return { icon: '🎯', title: `Next rank: ${bcRank.next_title}`, sub, action: 'recruit', kind: 'rank' };
    }
    if (hotArtists.length === 0) {
      return { icon: '🎵', title: `Share a playlist today — earn ${sound(rates, 'playlist_share')}`, sub: 'Daily reward available now.', action: 'playlist' };
    }
    return { icon: '🔥', title: `${hotArtists[0]?.artist_name || 'An artist'} is hot — recruit before someone else does`, sub: 'Check Discover for trending talent.', action: 'recruit' };
  }, [recruitCount, hotArtists, bcRank, rates]);

  const handleNextMoveClick = () => {
    if (nextMove.action === 'recruit') navigate('/player/discover');
    else if (nextMove.action === 'playlist') navigate('/playlists');
  };

  // ── Handle scroll-to from RexTab (Sprint 6A) ──────────────────────────────
  useEffect(() => {
    const target = sessionStorage.getItem('sounddao_scroll_to');
    if (!target) return;
    sessionStorage.removeItem('sounddao_scroll_to');
    const timer = setTimeout(() => {
      if (target === 'playlist') navigate('/playlists');
      if (target === 'roster') rosterSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(timer);
  }, [navigate]);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard dashboard--player">

      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-brand"><BrandMark /> SoundDAO <span className="version-badge">Beta v1.0</span></div>
        <div className="dashboard-header-actions">
          <a
            href="https://soundcoin.co/whitepaper"
            target="_blank"
            rel="noopener noreferrer"
            className="dashboard-header-link"
            title="Read the White Paper"
          >
            📄 White Paper
          </a>
          <button className="dashboard-settings-btn" onClick={onSettings} title="Profile & Settings">👤</button>
        </div>
      </header>

      {/* ══════ Player card (Sprint 6A polish, Option A): who + global rank, then 4 stats ══════ */}
      <section className="pc-card" aria-label="Your player card">
        <div className="pc-top">
          <div className="pc-avatar" aria-hidden="true">{initialsFor(getDisplayName(user))}</div>
          <div className="pc-who">
            <div className="pc-name-row">
              <h1 className="pc-name">{getDisplayName(user)}</h1>
              {currentRank.title && (
                <span className="pc-rank-chip">{currentRank.title} · Lv {currentRank.number}</span>
              )}
            </div>
            {/* Not when the banner below already is the rank goal. */}
            {bcRank?.next_title && nextMove.kind !== 'rank' && <div className="pc-next">Next: {bcRank.next_title}</div>}
          </div>
          <div className="pc-global">
            <div className="pc-label">Global rank</div>
            <div className="pc-global-num">{loadingData ? '—' : globalRank ? `#${globalRank}` : '—'}</div>
          </div>
        </div>

        <div className="pc-body">
          <div className="pc-cell">
            <div className="pc-label">SOUND</div>
            <div className="pc-val pc-val--gold">{loadingData ? '—' : balance.toLocaleString()}</div>
            {todayEarnings > 0 && <div className="pc-sub pc-sub--gold">+{todayEarnings} today</div>}
          </div>
          <div className="pc-cell">
            <div className="pc-label">Creators</div>
            <div className="pc-val">{recruitCount}</div>
            <div className="pc-sub">{scoutBadgeCount} scout badge{scoutBadgeCount === 1 ? '' : 's'}</div>
          </div>
          <div className="pc-cell">
            <div className="pc-label">Streak</div>
            {hotStreakActive ? (
              <>
                <div className="pc-val pc-val--hot">Hot 🔥</div>
                <div className="pc-sub">{hotStreakLabel ? `1.5x · expires in ${hotStreakLabel}` : '1.5x on everything'}</div>
              </>
            ) : (
              <>
                <div className="pc-val pc-val--hot">🔥 {streakCount}d</div>
                <div className="pc-sub">Log in tomorrow</div>
              </>
            )}
          </div>
          <div className="pc-cell">
            <div className="pc-label">Vault</div>
            <div className="pc-val">{vaultBalance}</div>
            <div className="pc-bar"><div className="pc-bar-fill" style={{ width: `${Math.min(vaultLoginCount / 30, 1) * 100}%` }} /></div>
            <div className="pc-sub">
              {vaultLockedUntil
                ? `Unlocks in ${Math.max(0, Math.ceil((new Date(vaultLockedUntil) - new Date()) / 86400000))}d`
                : 'SOUND locked'}
            </div>
          </div>
        </div>
      </section>

      {/* ══════ Your Next Move (Sprint 6A) ══════ */}
      <div className="dashboard-next-move" onClick={handleNextMoveClick}>
        <div className="dashboard-next-move-icon">{nextMove.icon}</div>
        <div className="dashboard-next-move-body">
          <p className="dashboard-next-move-title">{nextMove.title}</p>
          <p className="dashboard-next-move-sub">{nextMove.sub}</p>
        </div>
        <span className="dashboard-next-move-arrow">→</span>
      </div>

      <div className="pc-missions">
        <MissionCard userId={user_id} persona="player" onAction={handleMissionAction} />
      </div>

      {/* ══════ Hot Artists (Sprint 5C Part 2) ══════ */}
      <div className="dashboard-section">
        <h2 className="dashboard-section-title">🔥 Hot right now</h2>
        {hotArtists.length === 0 ? (
          <div className="dashboard-empty-state" style={{ textAlign: 'center', padding: '12px' }}>
            <p>You're ahead of the curve. Check Discover for new talent.</p>
            <button
              className="dashboard-btn dashboard-btn--primary"
              style={{ marginTop: '8px' }}
              onClick={() => navigate('/creators')}
            >
              Discover creators
            </button>
          </div>
        ) : (
          <div>
            {hotArtists.map(artist => {
              const hc = HEAT_COLORS[artist.heat_tier] || HEAT_COLORS.Cool;
              const initials = (artist.artist_name || '?').slice(0, 2).toUpperCase();
              return (
                <div key={artist.id} className="hot-artist-item">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div className="roster-avatar" style={{ background: hc.bg, color: hc.color }}>
                      {initials}
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
                        {artist.artist_name || 'Artist'}
                      </p>
                      <p style={{ margin: 0, fontSize: '11px', opacity: 0.5 }}>
                        {artist.genre || 'Music'}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontSize: '10px', fontWeight: '600', padding: '2px 8px',
                      borderRadius: '999px', background: hc.bg, color: hc.color,
                    }}>
                      {artist.heat_tier}
                    </span>
                    <button
                      className="dashboard-btn dashboard-btn--primary"
                      style={{ fontSize: '11px', padding: '4px 10px' }}
                      onClick={() => navigate(`/creator/${artist.user_id}`)}
                    >
                      Recruit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ══════ Creator Roster — reskinned (Sprint 5C Part 2) ══════ */}
      <div ref={rosterSectionRef} className="dashboard-section">
        <h2 className="dashboard-section-title">Your Creator Roster</h2>

        {loadingData ? (
          <div className="dashboard-section-loading">Loading…</div>
        ) : rosterEntries.length === 0 ? (
          <p className="dashboard-recruit-empty">
            No creators yet. Share your link below or check your missions to get started.
          </p>
        ) : (
          <div className="dashboard-roster">
            {rosterEntries.map(entry => {
              const hasBadge = rosterScoutBadges[entry.user_id];
              const hc = entry.profileComplete
                ? (HEAT_COLORS[entry.heat_tier] || HEAT_COLORS.Cool)
                : HEAT_COLORS.Cool;
              const name = entry.profileComplete
                ? (entry.artist_name || 'Artist')
                : 'Artist (profile incomplete)';
              const initials = (entry.artist_name || '?').slice(0, 2).toUpperCase();
              return (
                <div key={entry.user_id} className="dashboard-roster-item">
                  <div className="roster-avatar" style={entry.profileComplete ? { background: hc.bg, color: hc.color } : {}}>
                    {initials}
                  </div>
                  <div className="roster-info">
                    <p className="roster-name">
                      {name}
                      {hasBadge && ' 🏅'}
                    </p>
                    {/* SEASON ZERO: no more "RAW · COLD". Stage shows at Flair+,
                        heat at Warm+; if neither qualifies the line is omitted. */}
                    {(() => {
                      if (!entry.profileComplete) {
                        return <p className="roster-rank">Pending setup</p>;
                      }
                      const label = publicStageHeatLabel(entry.stage, entry.heat_tier);
                      return label ? <p className="roster-rank">{label}</p> : null;
                    })()}
                  </div>
                  {entry.profileComplete && isHeatPublic(entry.heat_tier) && (
                    <span style={{
                      fontSize: '10px', fontWeight: '600', padding: '2px 8px',
                      borderRadius: '999px', background: hc.bg, color: hc.color,
                    }}>
                      {entry.heat_tier}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ══════ Playlists (Sprint 6A: moved to /playlists) ══════ */}
      <div className="dashboard-section">
        <h2 className="dashboard-section-title">🎵 Playlists</h2>
        <Link to="/dig" className="ax-dig-entry" style={{ width: '100%' }}>
          <span className="ax-dig-entry-icon" aria-hidden="true">📼</span>
          <span style={{ flex: 1 }}>
            <span className="ax-dig-entry-title" style={{ display: 'block' }}>Dig the Archive</span>
            <span className="ax-dig-entry-sub" style={{ display: 'block' }}>Live tapes from 1,600+ bands. Build a playlist, find the next band first.</span>
          </span>
          <span aria-hidden="true">→</span>
        </Link>
        <Link to="/playlists" className="dashboard-next-move" style={{ width: '100%', marginTop: 10, textDecoration: 'none' }}>
          <div className="dashboard-next-move-icon">🎧</div>
          <div className="dashboard-next-move-body">
            <p className="dashboard-next-move-title">My Playlists</p>
            <p className="dashboard-next-move-sub">Your built playlists, and sharing a Spotify / Apple Music / YouTube link now live here.</p>
          </div>
          <span className="dashboard-next-move-arrow">→</span>
        </Link>
      </div>

      {/* ══════ Recruit & Refer ══════ */}
      <div ref={referralSectionRef} className="dashboard-section dashboard-recruit-section">
        <h2 className="dashboard-section-title">Recruit & Refer</h2>
        <div className="dashboard-recruit-tiers">
          <div className="dashboard-recruit-tier">
            <span className="dashboard-recruit-tier-reward">{plus(rates, 'recruit_creator')} SOUND</span>
            <span className="dashboard-recruit-tier-label">Recruit a creator</span>
          </div>
          <div className="dashboard-recruit-tier-divider" />
          <div className="dashboard-recruit-tier">
            <span className="dashboard-recruit-tier-reward">{plus(rates, 'refer_player')} SOUND</span>
            <span className="dashboard-recruit-tier-label">Refer a fan / player</span>
          </div>
        </div>
        <p className="dashboard-recruit-desc">
          Share your link. Earn SOUND for every creator or fan who signs up through it — paid once they confirm their email and make a first post, share or scout.
        </p>

        {referralLink ? (
          <>
            <div className="dashboard-referral-link-row">
              <span className="dashboard-referral-link-text">{referralLink}</span>
              <button
                className={`dashboard-referral-copy-btn ${copyDone ? 'dashboard-referral-copy-btn--done' : ''}`}
                onClick={handleCopyLink}
              >
                {copyDone ? '✓ Copied' : 'Copy'}
              </button>
            </div>

            <div className="dashboard-share-row">
              <button className="dashboard-share-btn dashboard-share-btn--twitter" onClick={handleShareTwitter}>
                <span>𝕏</span> Share on X
              </button>
              <button className="dashboard-share-btn dashboard-share-btn--whatsapp" onClick={handleShareWhatsApp}>
                <span>💬</span> Share on WhatsApp
              </button>
            </div>
          </>
        ) : (
          <p className="dashboard-referral-loading">Loading your referral link…</p>
        )}
      </div>

      {/* Edit Profile — mobile only */}
      <button
        className="dashboard-edit-profile-mobile"
        onClick={() => navigate('/settings')}
      >
        ✏️ Edit Profile
      </button>

      {/* Chatbot */}
      <Chatbot ref={chatRef} user={user} session={session} />

      {/* Rank-up modal */}
      {rankUpModal && (
        <div className="rankup-overlay" onClick={() => setRankUpModal(null)}>
          <div className="rankup-modal" onClick={e => e.stopPropagation()}>
            <div className="rankup-icon">🏆</div>
            <h2 className="rankup-title">Rank Up!</h2>
            <p className="rankup-new-rank">{rankUpModal.title}</p>
            <p className="rankup-subtitle">You're climbing the ranks. Keep stacking SOUND.</p>
            <button className="rankup-btn" onClick={() => setRankUpModal(null)}>
              Let's Go
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
